import {
  DeleteOutlined,
  GlobalOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import {
  EFeatureFlag,
  type IAdminWarehouse,
  type IProductFeatureFlag,
  type IProductStocks,
  type IProductWarehouse,
  type IWarehouseDetail,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Slider,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { featureFlagTags } from '../../fragments/feature-flag-tags';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import {
  downloaderSyncWarehouseProduct,
  downloaderUnassignWarehouseProduct,
} from '../../reducers/downloader/reducer';
import { stockUpdate } from '../../reducers/product/reducer';
import { SPACING } from '../../theme/tokens';
import ExternalLink from '../external-link';
import FormSubmitFooter from '../form-submit-footer';
import Link from '../link';

interface IForm {
  count: { [key: string]: number } | undefined;
  priceEUR: { [key: string]: number } | undefined;
  recommendedPriceEUR: { [key: string]: number | null } | undefined;
  preOrderDays: { [key: string]: number } | undefined;
  url: { [key: string]: string } | undefined;
  isSyncActive: { [key: string]: boolean } | undefined;
  syncCount: { [key: string]: boolean } | undefined;
  syncPrice: { [key: string]: boolean } | undefined;
  syncDimensions: { [key: string]: boolean } | undefined;
  endPriceMarginRate: { [key: string]: number } | undefined;
  nextDeliveryDate: Dayjs | null;
}

function Stock(): React.ReactElement {
  const dispatch = useAppDispatch();
  const productUUID = useSelector((s: RootState) => s.Product.uuid);
  const productInProcess = useSelector((s: RootState) => s.Product.inProcess);
  const productUpdatedAt = useSelector((s: RootState) => s.Product.updatedAt);
  const productWarehouses = useSelector(
    (s: RootState) => s.Product.productWarehouses,
  );
  const productNextDeliveryDate = useSelector(
    (s: RootState) => s.Product.nextDeliveryDate,
  );
  const productFeatureFlags = useSelector(
    (s: RootState) => s.Product.featureFlags,
  );
  const warehouseState = useSelector((s: RootState) => s.Warehouse);
  const featureFlagsState = useSelector((s: RootState) => s.FeatureFlags);

  const [form] = Form.useForm<IForm>();

  const [initialValues, setInitialValues] = useState({});
  const [inStockCount, setInStockCount] = useState<
    IProductStocks | undefined
  >();
  const [warehouseElements, setWarehouseElements] = useState<
    React.ReactElement[]
  >([]);
  const [showOnlyActiveWarehouses, setShowOnlyActiveWarehouses] =
    useState<boolean>(true);

  useEffect((): void => {
    const initialValuesUE: IForm = {
      count: {},
      priceEUR: {},
      recommendedPriceEUR: {},
      preOrderDays: {},
      url: {},
      isSyncActive: {},
      syncCount: {},
      syncPrice: {},
      syncDimensions: {},
      endPriceMarginRate: {},
      nextDeliveryDate: productNextDeliveryDate
        ? dayjs(productNextDeliveryDate)
        : null,
    };

    warehouseState.warehouses?.forEach(({ uuid }: IWarehouseDetail): void => {
      initialValuesUE.count![uuid] = 0;
      initialValuesUE.priceEUR![uuid] = 0;
      initialValuesUE.recommendedPriceEUR![uuid] = 0;
      initialValuesUE.isSyncActive![uuid] = false;
      initialValuesUE.preOrderDays![uuid] = 0;
      initialValuesUE.syncCount![uuid] = false;
      initialValuesUE.syncPrice![uuid] = false;
      initialValuesUE.syncDimensions![uuid] = false;
      initialValuesUE.endPriceMarginRate![uuid] = 1;
      initialValuesUE.url![uuid] = '';
    });

    productWarehouses.forEach(
      ({
        uuid,
        count,
        pricesEUR,
        preOrderDays,
        url,
        isSyncActive,
        syncPrice,
        syncCount,
        syncDimensions,
        endPriceMarginRate,
      }: IProductWarehouse): void => {
        initialValuesUE.count![uuid] = count;
        initialValuesUE.priceEUR![uuid] = pricesEUR.purchase;
        initialValuesUE.recommendedPriceEUR![uuid] = pricesEUR.recommended;
        initialValuesUE.isSyncActive![uuid] = isSyncActive;
        initialValuesUE.preOrderDays![uuid] = preOrderDays;
        initialValuesUE.syncCount![uuid] = syncCount;
        initialValuesUE.syncPrice![uuid] = syncPrice;
        initialValuesUE.syncDimensions![uuid] = syncDimensions;
        initialValuesUE.url![uuid] = url || '';
        initialValuesUE.endPriceMarginRate![uuid] = endPriceMarginRate || 1;
      },
    );

    const warehousesWithCount: IProductWarehouse[] = productWarehouses.filter(
      (wh: IProductWarehouse): boolean => wh.count > 0,
    );

    let internalCount: number = 0;
    let externalCount: number = 0;

    warehousesWithCount.forEach((wh: IProductWarehouse): void => {
      const warehouse: IWarehouseDetail | undefined =
        warehouseState.warehouses?.find(
          (w: IWarehouseDetail): boolean => w.uuid === wh.uuid,
        );

      if (!warehouse) {
        return;
      }

      if (warehouse.isInternal) {
        internalCount += wh.count;
      } else if (wh.warehouse.isPrimary && warehouse.uuid === wh.uuid) {
        externalCount += wh.count;
      }
    });

    const inStockCountUE: IProductStocks = {
      internal: internalCount,
      external: externalCount,
    };

    setInitialValues(initialValuesUE);
    setInStockCount(inStockCountUE);

    form.setFieldsValue(initialValuesUE);
  }, [productWarehouses, warehouseState.warehouses, productUpdatedAt]);

  useEffect((): void => {
    const warehouseElementsUE: React.ReactElement[] =
      warehouseState.warehouses
        ?.map((warehouse: IWarehouseDetail): React.ReactElement | undefined => {
          const productWarehouse: IProductWarehouse | undefined =
            productWarehouses.find(
              (w: IProductWarehouse): boolean => w.uuid === warehouse.uuid,
            );

          if (
            showOnlyActiveWarehouses &&
            (!warehouse.isActive || !productWarehouse?.warehouse.uuid)
          ) {
            return undefined;
          }

          let isWarehousePrimary: boolean = false;

          const productWarehouseFeatureFlags: IProductFeatureFlag[] =
            productFeatureFlags.filter(
              (ff: IProductFeatureFlag): boolean =>
                [
                  EFeatureFlag.FLAG_ACTION,
                  EFeatureFlag.FLAG_NEW,
                  EFeatureFlag.FLAG_TIP,
                  EFeatureFlag.WAREHOUSE_PRODUCT_HIDDEN,
                  EFeatureFlag.WAREHOUSE_PRODUCT_CHECK_SYNC_ACTIVE,
                  EFeatureFlag.WAREHOUSE_PRODUCT_CHECK_SYNC,
                  EFeatureFlag.WAREHOUSE_PRODUCT_LONG_TERM_OUT_OF_STOCK,
                ].includes(ff.code) &&
                ff.value?.warehouse?.uuid === warehouse.uuid,
            );

          const productWarehouseFeatureFlagsEls: React.ReactElement[] =
            featureFlagTags({
              productFeatureFlags: productWarehouseFeatureFlags,
              FeatureFlags: featureFlagsState,
              Warehouse: warehouseState,
            });

          let warehouseProductUrl: string | undefined;

          if (productWarehouse) {
            warehouseProductUrl = productWarehouse.url;

            isWarehousePrimary = productWarehouse.warehouse.isPrimary;
          }

          return (
            <Row gutter={SPACING.lg} key={warehouse.uuid}>
              <Divider titlePlacement="start">
                <Space size={SPACING.sm} align="start" orientation="horizontal">
                  <>
                    <Typography.Paragraph>
                      {warehouse.name}
                    </Typography.Paragraph>
                    <small>{warehouse.internalDescription}</small>
                    {productWarehouseFeatureFlagsEls}
                  </>
                </Space>
              </Divider>

              <Col span={1}>
                {isWarehousePrimary ? <Tag color="blue">Primary</Tag> : null}
              </Col>

              <Col span={2}>
                <Form.Item>
                  <Space.Compact>
                    <Form.Item name={['count', warehouse.uuid]} noStyle>
                      <InputNumber disabled={productInProcess} />
                    </Form.Item>
                    <Button type="default" style={{ pointerEvents: 'none' }}>
                      pcs
                    </Button>
                  </Space.Compact>
                </Form.Item>
              </Col>

              <Col span={2}>
                <Form.Item>
                  <Space.Compact>
                    <Form.Item name={['priceEUR', warehouse.uuid]} noStyle>
                      <InputNumber disabled />
                    </Form.Item>
                    <Button type="default" style={{ pointerEvents: 'none' }}>
                      €
                    </Button>
                  </Space.Compact>
                </Form.Item>
              </Col>

              <Col span={2}>
                <Form.Item>
                  <Space.Compact>
                    <Form.Item
                      name={['recommendedPriceEUR', warehouse.uuid]}
                      noStyle
                    >
                      <InputNumber disabled />
                    </Form.Item>
                    <Button type="default" style={{ pointerEvents: 'none' }}>
                      €
                    </Button>
                  </Space.Compact>
                </Form.Item>
              </Col>

              <Col span={1}>
                <Form.Item name={['preOrderDays', warehouse.uuid]}>
                  <InputNumber disabled={productInProcess} />
                </Form.Item>
              </Col>

              {!warehouse.isInternal && (
                <>
                  <Col span={6}>
                    <Form.Item name={['endPriceMarginRate', warehouse.uuid]}>
                      <Slider
                        min={0.5}
                        max={2}
                        step={0.005}
                        marks={{
                          0.5: '50%',
                          1: '100%',
                          1.5: '150%',
                          2: '200%',
                        }}
                        tooltip={{
                          formatter: (value: number): string =>
                            `${Math.floor(value * 1000) / 10}%`,
                        }}
                        disabled={
                          productInProcess ||
                          !productWarehouse?.warehouseProduct.uuid
                        }
                        onChange={(value: number): void => {
                          form.setFieldValue(
                            ['endPriceMarginRate', warehouse.uuid],
                            value,
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={1}>
                    <Link
                      to={`/downloader/warehouse-product/${productWarehouse?.warehouseProduct.uuid}`}
                      openInNewTab
                      disabled={
                        productInProcess ||
                        !productWarehouse?.warehouseProduct.uuid
                      }
                    />
                  </Col>

                  <Col span={3}>
                    <Form.Item>
                      <Space.Compact>
                        <Button
                          type="default"
                          style={{ pointerEvents: 'none' }}
                        >
                          URL
                        </Button>
                        <Form.Item name={['url', warehouse.uuid]} noStyle>
                          <Input disabled={productInProcess} />
                        </Form.Item>
                        {warehouseProductUrl ? (
                          <ExternalLink
                            to={warehouseProductUrl}
                            ariaLabel="Open external source URL in new tab"
                          />
                        ) : (
                          <Button
                            type="default"
                            disabled
                            icon={<GlobalOutlined />}
                          />
                        )}
                      </Space.Compact>
                    </Form.Item>
                  </Col>

                  <Col span={1}>
                    <Tooltip title="Is sync with external source active?">
                      <Form.Item
                        name={['isSyncActive', warehouse.uuid]}
                        valuePropName={
                          productWarehouse && productWarehouse.isSyncActive
                            ? 'checked'
                            : ''
                        }
                      >
                        <Switch />
                      </Form.Item>
                    </Tooltip>
                  </Col>

                  <Col span={1}>
                    <Tooltip title="Sync count with external source?">
                      <Form.Item
                        name={['syncCount', warehouse.uuid]}
                        valuePropName={
                          productWarehouse && productWarehouse.syncCount
                            ? 'checked'
                            : ''
                        }
                      >
                        <Switch />
                      </Form.Item>
                    </Tooltip>
                  </Col>

                  <Col span={1}>
                    <Tooltip title="Sync price with external source?">
                      <Form.Item
                        name={['syncPrice', warehouse.uuid]}
                        valuePropName={
                          productWarehouse && productWarehouse.syncPrice
                            ? 'checked'
                            : ''
                        }
                      >
                        <Switch />
                      </Form.Item>
                    </Tooltip>
                  </Col>

                  <Col span={1}>
                    <Tooltip title="Sync dimensions with external source?">
                      <Form.Item
                        name={['syncDimensions', warehouse.uuid]}
                        valuePropName={
                          productWarehouse && productWarehouse.syncDimensions
                            ? 'checked'
                            : ''
                        }
                      >
                        <Switch />
                      </Form.Item>
                    </Tooltip>
                  </Col>

                  <Col span={1}>
                    <Tooltip title="Sync product with warehouse">
                      <Button
                        color="primary"
                        onClick={(): void => {
                          if (
                            !productWarehouse ||
                            !productWarehouse.warehouseProduct.uuid
                          ) {
                            return;
                          }

                          dispatch(
                            downloaderSyncWarehouseProduct(
                              productUUID,
                              productWarehouse.warehouseProduct.uuid,
                            ),
                          );
                        }}
                        disabled={productInProcess || !productWarehouse}
                      >
                        Sync <ReloadOutlined />
                      </Button>
                    </Tooltip>
                  </Col>

                  <Col span={1}>
                    <Popconfirm
                      placement="bottom"
                      title="Unassign warehouse product?"
                      okText="Yes"
                      cancelText="No"
                      description="Product will be unassigned from warehouse product."
                      onConfirm={(): void => {
                        if (!productWarehouse) {
                          return;
                        }

                        dispatch(
                          downloaderUnassignWarehouseProduct(
                            productUUID,
                            warehouse.uuid,
                            productWarehouse.warehouseProduct.uuid,
                          ),
                        );
                      }}
                    >
                      <Button
                        color="danger"
                        variant="outlined"
                        disabled={productInProcess || !productWarehouse}
                      >
                        Unassign <DeleteOutlined />
                      </Button>
                    </Popconfirm>
                  </Col>
                </>
              )}
            </Row>
          );
        })
        .filter(notEmpty) || [];

    setWarehouseElements(warehouseElementsUE);
  }, [
    productInProcess,
    productWarehouses,
    warehouseState.warehouses,
    showOnlyActiveWarehouses,
    productUpdatedAt,
    featureFlagsState.featureFlags,
    productFeatureFlags,
  ]);

  const onFinish = useCallback(
    (
      {
        count,
        isSyncActive,
        nextDeliveryDate,
        preOrderDays,
        syncCount,
        syncPrice,
        syncDimensions,
        url,
        endPriceMarginRate,
      }: IForm,
      warehouses: IWarehouseDetail[] | undefined,
      pUUID: string,
    ): void => {
      if (!warehouses) {
        throw new Error('Warehouses not found!');
      }

      const warehousesFormatted: IAdminWarehouse[] = warehouses.map(
        (item: IWarehouseDetail): IAdminWarehouse => ({
          uuid: item.uuid,
          url: '',
          count: 0,
          pricesEUR: {
            purchase: 0,
            recommended: 0,
          },
          preOrderDays: 0,
          isSyncActive: false,
          syncCount: false,
          syncPrice: false,
          syncDimensions: false,
          endPriceMarginRate: 1,
        }),
      );

      Object.keys(count || {}).forEach((uuid: string): void => {
        const wf: IAdminWarehouse | undefined = warehousesFormatted.find(
          (w: IAdminWarehouse): boolean => w.uuid === uuid,
        );

        if (!wf) {
          throw new Error('Warehouse formatted not found!');
        }

        const u: string | undefined = url?.[uuid];
        const c: number = count?.[uuid] || 0;
        const i: boolean = isSyncActive?.[uuid] || false;
        const p: number = preOrderDays?.[uuid] || 0;
        const sp: boolean = syncPrice?.[uuid] || false;
        const sc: boolean = syncCount?.[uuid] || false;
        const sd: boolean = syncDimensions?.[uuid] || false;
        const e: number = endPriceMarginRate?.[uuid] || 1;

        wf.url = u?.trim() || '';
        wf.count = c;
        wf.isSyncActive = [true, 'checked'].includes(i);
        wf.preOrderDays = p;
        wf.syncPrice = sp;
        wf.syncCount = sc;
        wf.syncDimensions = sd;
        wf.endPriceMarginRate = e;
      });

      dispatch(
        stockUpdate(
          pUUID,
          nextDeliveryDate ? nextDeliveryDate.toDate() : undefined,
          warehousesFormatted,
        ),
      );
    },
    [dispatch],
  );

  return (
    <>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Stock ({inStockCount?.internal || 0} + {inStockCount?.external || 0} ={' '}
        {(inStockCount?.internal || 0) + (inStockCount?.external || 0)})
      </Typography.Title>

      <Row gutter={SPACING.lg}>
        <Switch
          checkedChildren="All warehouses"
          unCheckedChildren="Active warehouses"
          onChange={(): void =>
            setShowOnlyActiveWarehouses(!showOnlyActiveWarehouses)
          }
          value={!showOnlyActiveWarehouses}
        />
      </Row>

      <Row gutter={SPACING.lg}>
        <Col span={1}></Col>
        <Col span={2}>Count</Col>
        <Col span={2}>Purchase price EUR without VAT</Col>
        <Col span={2}>Recommended price EUR without VAT</Col>
        <Col span={1}>Preorder days</Col>
        <Col span={6}>End price margin rate</Col>
        <Col span={1}>Link</Col>
        <Col span={3}>URL</Col>
        <Col span={1}>
          Sync
          <br />
          WH
        </Col>
        <Col span={1}>
          Sync
          <br />
          count
        </Col>
        <Col span={1}>
          Sync
          <br />
          price
        </Col>
        <Col span={1}>
          Sync
          <br />
          dimensions
        </Col>
        <Col span={2}>
          Sync
          <br />
          WH product
        </Col>
      </Row>

      <Form
        form={form}
        onFinish={(values: IForm): void => {
          onFinish(values, warehouseState.warehouses, productUUID);
        }}
        layout="vertical"
        initialValues={initialValues}
      >
        {warehouseElements}

        <Row gutter={SPACING.lg}>
          <Col span={6}>
            <Form.Item name="nextDeliveryDate">
              <DatePicker
                placeholder="Next delivery date"
                disabled={productInProcess}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <FormSubmitFooter
              inProcess={productInProcess}
              updatedAt={productUpdatedAt}
              block
            />
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default React.memo(Stock);
